/**
 * The reports pane — one dynamic interface, not a grid of cards pointing at
 * six different pages. Switching a tab swaps the body in place; the URL's
 * `?tab=` just makes the active report link/back-button-able, it never
 * navigates to a different route or remounts the shell. Every tab shares one
 * filter/export row (`ReportFilterBar`) and exports identically as CSV or
 * PDF (`report-export.ts`, both from the same column spec server-side).
 *
 * Owner and tenant scope live here, one level up from the tabs, rather than
 * as local state inside each panel — "generate this for one owner" means
 * picking them once and having every report respect it, not re-selecting
 * the same owner on each tab. `?ownerId=`/`?tenantId=` (and the property
 * report's own required `?propertyId=`) persist the choice
 * across a tab switch the same way `?tab=` does; a panel that has no
 * lease/tenant concept in its output (Vacancy, Deposits held — grouped by
 * property, not by lease) simply doesn't accept `tenantId`, so switching to
 * one of those while a tenant is scoped just leaves that scope unused rather
 * than erroring.
 *
 * Rent roll and delinquency predate this pane (step 6) and keep their own
 * top-nav links under `ledger:read` rather than a tab here — a different
 * capability, and already prominent in their own right.
 */

import { useSearchParams } from 'react-router';
import { ArAging } from './ArAging.js';
import { Vacancy } from './Vacancy.js';
import { LeaseExpirations } from './LeaseExpirations.js';
import { DepositsHeld } from './DepositsHeld.js';
import { DepositsCollected } from './DepositsCollected.js';
import { ComplianceClocks } from './ComplianceClocks.js';
import { OwnerReport, PropertyReport, TenantReport } from './AccountReports.js';

const REPORTS = [
  { id: 'ar-aging', label: 'AR aging' },
  { id: 'vacancy', label: 'Vacancy' },
  { id: 'lease-expirations', label: 'Lease expirations' },
  { id: 'deposits-held', label: 'Deposits held' },
  { id: 'deposits-collected', label: 'Deposits collected', badge: 'Compliance' },
  { id: 'compliance-clocks', label: 'Compliance clocks' },
  { id: 'owner-report', label: 'Owner report' },
  { id: 'tenant-report', label: 'Tenant report' },
  { id: 'property-report', label: 'Property report' },
] as const;

type ReportId = (typeof REPORTS)[number]['id'];

function isReportId(value: string | null): value is ReportId {
  return REPORTS.some((r) => r.id === value);
}

export function ReportsHome() {
  const [searchParams, setSearchParams] = useSearchParams();
  const requested = searchParams.get('tab');
  const tab: ReportId = isReportId(requested) ? requested : 'ar-aging';
  const ownerId = searchParams.get('ownerId') ?? '';
  const tenantId = searchParams.get('tenantId') ?? '';
  const propertyId = searchParams.get('propertyId') ?? '';

  /** Merges into the current search params rather than replacing them, so
   *  switching tabs doesn't drop the owner/tenant scope and vice versa. */
  function patch(next: Record<string, string>): void {
    const merged = new URLSearchParams(searchParams);
    for (const [key, value] of Object.entries(next)) {
      if (value) merged.set(key, value);
      else merged.delete(key);
    }
    setSearchParams(merged);
  }

  const setOwnerId = (value: string) => patch({ ownerId: value });
  const setTenantId = (value: string) => patch({ tenantId: value });
  const setPropertyId = (value: string) => patch({ propertyId: value });

  return (
    <section className="stack">
      <h1>Reports</h1>
      <p className="muted">Live views over current data.</p>

      <div className="tabs" role="tablist" aria-label="Reports">
        {REPORTS.map((report) => (
          <button
            key={report.id}
            type="button"
            role="tab"
            aria-selected={report.id === tab}
            className={report.id === tab ? 'tab tab--active' : 'tab'}
            onClick={() => patch({ tab: report.id })}
          >
            {report.label}
            {'badge' in report ? <span className="pill"> {report.badge}</span> : null}
          </button>
        ))}
      </div>

      {tab === 'ar-aging' ? (
        <ArAging
          ownerId={ownerId}
          onOwnerChange={setOwnerId}
          tenantId={tenantId}
          onTenantChange={setTenantId}
        />
      ) : null}
      {tab === 'vacancy' ? <Vacancy ownerId={ownerId} onOwnerChange={setOwnerId} /> : null}
      {tab === 'lease-expirations' ? (
        <LeaseExpirations
          ownerId={ownerId}
          onOwnerChange={setOwnerId}
          tenantId={tenantId}
          onTenantChange={setTenantId}
        />
      ) : null}
      {tab === 'deposits-held' ? (
        <DepositsHeld ownerId={ownerId} onOwnerChange={setOwnerId} />
      ) : null}
      {tab === 'deposits-collected' ? (
        <DepositsCollected
          ownerId={ownerId}
          onOwnerChange={setOwnerId}
          tenantId={tenantId}
          onTenantChange={setTenantId}
        />
      ) : null}
      {tab === 'compliance-clocks' ? (
        <ComplianceClocks
          ownerId={ownerId}
          onOwnerChange={setOwnerId}
          tenantId={tenantId}
          onTenantChange={setTenantId}
        />
      ) : null}
      {tab === 'owner-report' ? <OwnerReport ownerId={ownerId} onOwnerChange={setOwnerId} /> : null}
      {tab === 'tenant-report' ? (
        <TenantReport tenantId={tenantId} onTenantChange={setTenantId} />
      ) : null}
      {tab === 'property-report' ? (
        <PropertyReport propertyId={propertyId} onPropertyChange={setPropertyId} />
      ) : null}
    </section>
  );
}
